import { type PowerSyncDrizzleDB, user } from "@habits/powersync";
import { patchRowVerified, type VerifiedWriteOptions } from "../powersync/verified-write";

/**
 * ── `user` is NOT excluded from `patchRowVerified` — audit 2026-08-13,
 * updated the same day after the `ThemeProvider` seed guard ──
 *
 * Every `UPDATE … WHERE id = ?` in this file goes through `patchRowVerified`,
 * deliberately DIFFERENT from the "server creates the row" criterion in other
 * portfolio repositories: here `user` cannot be assumed settled merely because
 * the server created it during registration (`packages/core-rust/src/db/user.rs:101-102`
 * `create_user_with_password`, `:350-351` `create_user_with_social`). Two
 * factors open the window, and only one has been closed:
 *
 *   - `db` (the PowerSync accessor) becomes non-null as soon as local SQLite
 *     opens, not when the initial sync finishes—and `userId()` becomes active
 *     as soon as the server responds to registration, an independent REST call
 *     from the PowerSync stream. Between that point and the checkpoint bringing
 *     in the server-created `user` row is a window where a normal `UPDATE`
 *     (change language, theme…) hits a row the local view does not yet have—it
 *     matches ZERO rows and silently returns "success". This window is STILL
 *     OPEN today: it does not depend on anyone seeding a local row; it is simply
 *     the view synchronizing.
 *
 *   - (Historical context, CLOSED) Until today's guard, `ThemeProvider`
 *     (`apps/web/src/providers/theme-provider.tsx`) worsened that same window:
 *     its seeding `createEffect` made a fallback LOCAL `db.insert(user)` for
 *     ANY `uid`, not just the guest—including a newly registered real account
 *     if that device opened the app before the server-created row arrived via
 *     sync (which is why it caught `UNIQUE constraint failed`: sometimes the
 *     local INSERT won the race, sometimes the server; idempotent by UNIQUE
 *     INDEX, not by content key—unlike Class 2 of `habit_completion`). The
 *     `uid !== VISITOR_ID` guard in `theme-provider.tsx` closed THIS specific
 *     cause: the effect now seeds only for the guest.
 *
 * **Closing phantom seeding does NOT make this protection unnecessary**—the
 * first point above still opens the same window for any early write, with or
 * without a phantom row. `patchRowVerified` here is generic checkpoint defense
 * in depth, not a patch left attached to an already-closed bug—if it is ever
 * removed, that should be because the first window is proven gone, not because
 * `ThemeProvider` stopped seeding phantoms. Full measurement in
 * `__tests__/theme-provider-seed-window.test.ts`.
 */
export async function updateUserLocaleMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  locale: string,
  options?: VerifiedWriteOptions,
): Promise<void> {
  await patchRowVerified(db, user, user.id, userId, { locale }, options);
}

/**
 * Shared Drizzle mutation for updating User font family preference.
 */
export async function updateUserFontFamilyMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  fontFamilyId: string | null,
  options?: VerifiedWriteOptions,
): Promise<void> {
  await patchRowVerified(db, user, user.id, userId, { font_family: fontFamilyId }, options);
}

/**
 * Writes the avatar KEY to the local row.
 *
 * It is written local-first (like language, font, and theme), not through REST:
 * the new photo appears instantly and survives a cold reload while offline. The
 * BINARY does go through REST (`POST /api/avatar`) because it does not fit in
 * the sync queue; only the key returned by that upload travels through here.
 *
 * `null` = "remove the photo" (initials return).
 *
 * Guests have no syncable row of their own and cannot have an avatar (rule
 * zero), so there is no `VISITOR_ID` branch here as in `locale`/`theme`.
 */
export async function updateUserImageMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  imageKey: string | null,
  options?: VerifiedWriteOptions,
): Promise<void> {
  await patchRowVerified(db, user, user.id, userId, { image: imageKey }, options);
}

/**
 * Shared Drizzle mutation for updating global theme.
 */
export async function updateGlobalThemeMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  themeId: string | null,
  options?: VerifiedWriteOptions,
): Promise<void> {
  await patchRowVerified(db, user, user.id, userId, { global_theme_id: themeId }, options);
}

/**
 * Shared Drizzle mutation for updating blur effect preference.
 */
export async function updateBlurMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  isBlur: boolean,
  options?: VerifiedWriteOptions,
): Promise<void> {
  await patchRowVerified(db, user, user.id, userId, { is_blur: isBlur ? 1 : 0 }, options);
}

/**
 * Shared Drizzle mutation for updating follow device appearance preference.
 */
export async function updateFollowDeviceThemeMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  followDevice: boolean,
  options?: VerifiedWriteOptions,
): Promise<void> {
  await patchRowVerified(
    db,
    user,
    user.id,
    userId,
    { follow_device_theme: followDevice ? 1 : 0 },
    options,
  );
}
